import React, { Fragment, useContext, useMemo, useState, useEffect, useRef } from 'react'
import { AppContext } from '../appContext'
import { selectChecklistItemsByPhase } from '../selectors/checklistItems'
import ChecklistItemFundRequest from './checklist-item-fund-request'
import ChecklistDocItemUpload from './checklist-doc-item-upload'
import { FaAngleRight, FaAngleDown } from 'react-icons/fa6'
import {
  ChecklistPhase,
  FundReleaseType,
  Adders,
  IChecklistItem,
  ChecklistItemType,
  StatusData,
  LoanFundingPhaseShared,
} from '@oneethos/shared'

type LoanFundingPhaseProps = {
  project: StatusData
  onProjectChange?: (p: StatusData) => void
}

type GroupedItems = {
  fundReleaseType: FundReleaseType
  checklistItems: IChecklistItem[]
}

const getFundReleaseDisplayName = (type: FundReleaseType): string => {
  return type.replace("Funding ", "")
}

const groupChecklistItemsByFundRelease = (project: StatusData, checklistItems: IChecklistItem[]): GroupedItems[] => {
  const activeTypes = Object.keys(project?.fundReleaseAmounts ?? {})
    .filter(key => (project?.fundReleaseAmounts?.[key as FundReleaseType] ?? 0) > 0) as FundReleaseType[]

  const hasFullFunding = activeTypes.includes(FundReleaseType.FullFundingRelease)
  const roofPrice = new Adders(project.adders).getRoofPrice()

  const filteredItems = checklistItems.filter(item => {
    if (hasFullFunding && roofPrice === 0) {
      return !item.fundReleaseTypes?.includes(FundReleaseType.RoofFundingRelease)
    }
    return true
  })

  return activeTypes.map(type => ({
    fundReleaseType: type,
    checklistItems: filteredItems.filter(item => item.fundReleaseTypes?.includes(type)),
  }))
}

export const LoanFundingPhase = ({ project, onProjectChange }: LoanFundingPhaseProps) => {
  const { state } = useContext(AppContext)
  const fundingPhaseItems = selectChecklistItemsByPhase(state, ChecklistPhase.LoanFunding)
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [manuallyExpandedTypes, setManuallyExpandedTypes] = useState<Record<string, boolean>>({})
  const isLocked = !project.nTPGiven
  
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!hasInitialized.current) {
      const allItemIds = fundingPhaseItems.map(item => item.id)

      setExpandedRows(allItemIds)
      hasInitialized.current = true
    }
  }, [fundingPhaseItems, project])

  const toggleRow = (id: string) => {
    setExpandedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id])
  }

  const toggleFundReleaseType = (fundReleaseType: string) => {
    setManuallyExpandedTypes(prev => ({
      ...prev,
      [fundReleaseType]: !prev[fundReleaseType],
    }))
  }

  const groupedChecklistItems = useMemo(
    () => groupChecklistItemsByFundRelease(project, fundingPhaseItems),
    [project, fundingPhaseItems]
  )

  const loanFundingReleaseGroups = LoanFundingPhaseShared.getLoanFundingReleaseGroups(groupedChecklistItems, project)

  return (
    <div className='table-responsive'>
      <table className={`table ${isLocked && 'gray-out'}`}>
        <tbody>
          {loanFundingReleaseGroups.map(({ group: { fundReleaseType, checklistItems }, disabled, active }) => {  
            // Check if this type is manually expanded or automatically active
            const isManuallyExpanded = manuallyExpandedTypes[fundReleaseType]
            const shouldShowItems = active || isManuallyExpanded
            
            return (
              <Fragment key={fundReleaseType}>
                <tr 
                  className={`text-center pointer ${disabled ? `gray-out` : ''}`}
                  onClick={() => toggleFundReleaseType(fundReleaseType)}
                  {...(disabled ? { inert: "" } : {})}
                >
                  <td colSpan={5} className="fw-bold p-2">
                    {getFundReleaseDisplayName(fundReleaseType)}
                    <span className="ms-2">
                      {shouldShowItems ? <FaAngleDown /> : <FaAngleRight />}
                    </span>
                  </td>
                </tr>
                {shouldShowItems && checklistItems.map(item => {
                  const isExpanded = expandedRows.includes(item.id) && !disabled

                  return (
                    <Fragment key={item.id}>
                      {(item.type === ChecklistItemType.FundingRequest || item.type === ChecklistItemType.Airtable) &&
                        <ChecklistItemFundRequest
                          item={item}
                          project={project}
                          onClick={() => toggleRow(item.id)}
                          isExpanded={isExpanded}
                          onProjectChange={onProjectChange}
                        />
                      }
                      {item.type === ChecklistItemType.DocumentUpload &&
                        <ChecklistDocItemUpload
                          item={item}
                          project={project}
                          handleRowToggle={() => toggleRow(item.id)}
                          isExpanded={isExpanded}
                          onProjectChange={onProjectChange}
                        />
                      }
                    </Fragment>
                  )
                })}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
